import React, { useCallback, useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import ExistingBarcodesModal, { BarcodeItem } from "./ExistingBarcodesModal";
import McKessonPageHeader from "../McKessonPageHeader/McKessonPageHeader";
import useMckesson from "../../../Hooks/useMckesson";
import { useItemSearchMutation, useGetItemCreateDropdownValMutation, useCreateInventortItemMutation, useInventoryTransactionsReceiveMutation, useInventoryTransactionMutation } from "../../../redux/inventory/inventoryApiSlice";
import { getErrorMessage } from "../../../utils/commonUtils";
import { useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import "./McOrderProcessor.scss";

interface OrderFile {
  id: string;
  fileName: string;
  fileContent: string;
  timestamp: string;
  loaded: boolean;
  processed: boolean;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
}

interface OrderDataItem {
  id: string;
  fileId: string;
  hlId: number;
  vc: string;
  mg: string;
  lot: string | null;
  ndc: string | null;
  qtyOrdered: number;
  qtyshipped: number;
  uom: string;
  qtyShippedInvUom: number;
  qtyReceived: number;
  invUom: string;
  itemDescription: string;
  expDate: string | null;
  processed: boolean;
  reason: string | null;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string | null;
  createdBy: string;
  updatedBy: string | null;
}

interface OrderInfo {
  orderNumber: string;
  orderDate: string;
  trackingNumber: string;
  fileName: string;
  location: {
    locationId: string;
    locationCode: string;
    locationName: string;
  };
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const getOrderInfoFromHeader = (headerData: any, fileName: string): OrderInfo => {
  const stParty = headerData.ediParties?.find(
    (p: any) => p.identifierCode === "ST"
  );
  return {
    orderNumber: headerData.purchaseOrderNumber || "",
    orderDate: formatDate(headerData.purchaseOrderDate),
    trackingNumber: headerData.trackingNumber || "",
    fileName: headerData.mcKessonEdiFile?.fileName || fileName,
    location: {
      locationId: headerData.location?.id || "",
      locationCode: headerData.location?.locationCode || "",
      locationName: headerData.location?.locationName || "",
    },
  };
};

const defaultFilter = { searchText: "", active: true };

const McOrderProcessor: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [selectedStock, setSelectedStock] = useState("");
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
  const [dataItems, setDataItems] = useState<OrderDataItem[]>([]);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string>("");
  const [showBarcodePopup, setShowBarcodePopup] = useState(false);
  const [barcodeItems, setBarcodeItems] = useState<BarcodeItem[]>([]);
  const [stocks, setStocks] = useState<{ id: string; stockCode: string; stockName: string }[]>([]);
  const [stockError, setStockError] = useState("");
  const [selectedDataItem, setSelectedDataItem] = useState<OrderDataItem | null>(null);
  const [receiveTransactionId, setReceiveTransactionId] = useState<string>("");

  const {
    getMckessonEdiFileListAPI,
    getMckessonEdiFileListLoading,
    getMckessonEdiItemListAPI,
    getMckessonEdiItemListLoading,
    getMckessonEdiHeaderListAPI,
    getMckessonEdiHeaderListLoading,
    markMckessonOrderProcessedAPI,
    markMckessonOrderProcessedLoading,
  } = useMckesson();

  const [itemSearch, { isLoading: fetchLoading }] =
    useItemSearchMutation();
  const [getDropdownVal] = useGetItemCreateDropdownValMutation();
  const [createItem, { isLoading: createItemLoading }] =
    useCreateInventortItemMutation();
  const [inventoryTransactionsReceive, { isLoading: receiveLoading }] =
    useInventoryTransactionsReceiveMutation();
  const [getInventoryTransactions] = useInventoryTransactionMutation();
  const currentUserId = useAppSelector(selectCurrentUserId);

  const isLoading =
    getMckessonEdiFileListLoading || getMckessonEdiItemListLoading || getMckessonEdiHeaderListLoading || fetchLoading || markMckessonOrderProcessedLoading || createItemLoading || receiveLoading;

  const loadFiles = useCallback(async () => {
    try {
      const result = await getMckessonEdiFileListAPI({
        searchText: searchVal.searchText,
        active: searchVal.active,
      });
      if (result) {
        setOrderFiles(result);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [searchVal]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const loadStocks = async () => {
      try {
        const res = await getDropdownVal({}).unwrap();
        if (res?.result?.stocks) {
          setStocks(res.result.stocks);
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };
    loadStocks();
  }, [getDropdownVal]);

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        const res = await getInventoryTransactions({}).unwrap();
        if (res?.result) {
          const receiveTransaction = res.result.find(
            (t: any) => t.transactionName?.toLowerCase() === "receive"
          );
          if (receiveTransaction) {
            setReceiveTransactionId(receiveTransaction.id);
          }
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };
    loadTransactions();
  }, [getInventoryTransactions]);

  const handleFileClick = async (file: OrderFile) => {
    setSelectedFileId(file.id);
    try {
      const [itemResult, headerResult] = await Promise.all([
        getMckessonEdiItemListAPI({ fileId: file.id }),
        getMckessonEdiHeaderListAPI({ fileId: file.id }),
      ]);
      if (itemResult) {
        setDataItems(itemResult ?? []);
      }
      if (headerResult?.length > 0) {
        const headerData = headerResult[0];
        setOrderInfo(getOrderInfoFromHeader(headerData, file.fileName));
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleFetch = async (item: OrderDataItem) => {
    if (!selectedStock) {
      setStockError("Stock is required");
      return;
    }
    setStockError("");
    setSelectedDataItem(item);
    try {
      const res = await itemSearch({
        searchText: "",
        active: true,
        includeDeleted: false,
        ndcCode: item.ndc || "",
        lotNo: item.lot || "",
        expDate: item.expDate || "",
        productNo: "",
        sourceRcv: selectedStock,
        sortColumn: "",
        sortDirection: "",
      }).unwrap();
      if (res?.result?.length > 0) {
        const mappedItems: BarcodeItem[] = res.result.map((r: any) => ({
          id: r.id,
          barcode: r.rxSRXId,
          ndc: r.ndcCode,
          lot: r.lotNo,
          expDate: r.expiryDate,
          source: r.stock?.stockCode || "",
          orderingProductNo: r.orderingProductMaster?.productNo || "",
        }));
        setBarcodeItems(mappedItems);
        setShowBarcodePopup(true);
      } else {
        setBarcodeItems([]);
        setShowBarcodePopup(true);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBarcodeSave = async (selectedBarcode: BarcodeItem, qty: string, refInfo: string) => {
    const selectedStockObj = stocks.find((s) => s.stockCode === selectedStock);
    if (!selectedStockObj) {
      toast.error("Please select a valid stock");
      return;
    }
    try {
      const payload = {
        quantity: Number(qty),
        referenceNumber: refInfo,
        transactionId: receiveTransactionId,
        locationId: orderInfo?.location.locationId || "",
        itemId: selectedBarcode.id,
        userId: currentUserId,
        isActive: true,
      };
      const res = await inventoryTransactionsReceive(payload).unwrap();
      if (res?.success) {
        toast.success(res.message || "Inventory received successfully");
      } else {
        toast.success("Inventory received successfully");
      }
      setShowBarcodePopup(false);
      handleFileClick({ id: selectedFileId } as OrderFile);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBarcodeCancel = () => {
    setShowBarcodePopup(false);
  };

  const handleNewBarcodeSave = async (values: any) => {
    try {
      const payload = {
        rxSRXId: values.barcode,
        rxName: values.rxName,
        drugName: values.rxGroup,
        ndcCode: values.ndcCode,
        manufacturer: values.manufacturer,
        lotNo: values.lotNumber,
        expiryDate: values.expirationDate
          ? new Date(values.expirationDate).toISOString()
          : "",
        unitId: values.inventoryUnit,
        rxTypeId: values.itemType,
        stockId: values.source,
        cptCode: "0",
        notes: values.notes,
        isActive: true,
        isRecalled: false,
        isNeverExpire: false,
        isKit: false,
        isSerialTracked: false,
        createdBy: currentUserId,
        orderingProductMasterId: null,
        cvx: "",
        userDef1: "",
        userDef2: "",
        userDef3: "",
        userDef4: "",
        userDef5: "",
        useUnits: [],
        customFieldValues: {},
      };
      const res = await createItem(payload).unwrap();
      if (res?.success) {
        toast.success(res.message || "Barcode created successfully");
        // Refresh the barcodes list via item search API
        const searchRes = await itemSearch({
          searchText: "",
          active: true,
          includeDeleted: false,
          ndcCode: selectedDataItem?.ndc || "",
          lotNo: selectedDataItem?.lot || "",
          expDate: selectedDataItem?.expDate || "",
          productNo: "",
          sourceRcv: selectedStock,
          sortColumn: "",
          sortDirection: "",
        }).unwrap();
        if (searchRes?.result?.length > 0) {
          const mappedItems: BarcodeItem[] = searchRes.result.map((r: any) => ({
            id: r.id,
            barcode: r.rxSRXId,
            ndc: r.ndcCode,
            lot: r.lotNo,
            expDate: r.expiryDate,
            source: r.stock?.stockCode || "",
            orderingProductNo: r.orderingProductMaster?.productNo || "",
          }));
          setBarcodeItems(mappedItems);
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMarkProcessed = async (item: OrderDataItem) => {
    try {
      await markMckessonOrderProcessedAPI({
        fileId: selectedFileId,
        itemId: item.id,
      });
      handleFileClick({ id: selectedFileId } as OrderFile);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <div className="mc-order-processor-wrapper">
      {isLoading && <Loading />}

      <McKessonPageHeader
        title="McKesson Order Processor"
        disRecords={orderFiles.length}
        totalRecords={orderFiles.length}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
        defaultFilter={defaultFilter}
        showCheckbox={true}
        checkboxLabel="Pending"
      />

      {/* Files Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>File Name</th>
              <th>Timestamp</th>
              <th>Processed?</th>
              <th>Created By</th>
              <th>Edited By</th>
            </tr>
          </thead>
          <tbody>
            {orderFiles.length > 0 ? (
              orderFiles.map((file) => (
                <tr
                  key={file.id}
                  className={selectedFileId === file.id ? "selected" : ""}
                  onClick={() => handleFileClick(file)}
                >
                  <td>
                    <span className="file-link">{file.fileName}</span>
                  </td>
                  <td>{file.timestamp}</td>
                  <td>{file.processed ? "Yes" : "No"}</td>
                  <td>{file.createdBy}</td>
                  <td>{file.updatedBy}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Data Items Section */}
      {orderInfo && (
        <div className="data-items-section">
          <h5 className="section-title">Data Items</h5>

          {/* Order Info */}
          <div className="order-info-grid">
            <div className="order-info-row">
              <div className="info-item">
                <label>Order # (SRX):</label>
                <span>{orderInfo.orderNumber}</span>
              </div>
              <div className="info-item">
                <label>Order Date:</label>
                <span>{orderInfo.orderDate}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Tracking #:</label>
                <span>{orderInfo.trackingNumber}</span>
              </div>
              <div className="info-item">
                <label>File Name:</label>
                <span>{orderInfo.fileName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Ordering Location Code:</label>
                <span>{orderInfo.location.locationCode}</span>
              </div>
              <div className="info-item">
                <label>Ordering Location Name:</label>
                <span>{orderInfo.location.locationName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Stock:</label>
                <div>
                  <Form.Select
                    size="sm"
                    value={selectedStock}
                    onChange={(e) => {
                      setSelectedStock(e.target.value);
                      if (e.target.value) setStockError("");
                    }}
                    className="stock-select"
                    isInvalid={!!stockError}
                  >
                    <option value="">Select</option>
                    {stocks.map((stock) => (
                      <option key={stock.id} value={stock.stockCode}>
                        {stock.stockName}
                      </option>
                    ))}
                  </Form.Select>
                  {stockError && (
                    <div className="text-danger" style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                      {stockError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Data Items Table */}
          <div className="listing-table-wrapper">
            <Table responsive="md" hover={true}>
              <thead>
                <tr>
                  <th>HL Id</th>
                  <th>VC</th>
                  <th>MG</th>
                  <th>Lot</th>
                  <th>NDC</th>
                  <th>Qty Ordered</th>
                  <th>Qty Shipped</th>
                  <th>UOM</th>
                  <th>Qty Shipped (Inv. Uom)</th>
                  <th>Qty Received</th>
                  <th>Inv. Uom</th>
                  <th>ItemDescription</th>
                  <th>ExpDate</th>
                  <th>Processed?</th>
                  <th>Reason</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dataItems.length > 0 ? (
                  dataItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.hlId}</td>
                      <td>{item.vc}</td>
                      <td>{item.mg}</td>
                      <td>{item.lot}</td>
                      <td>{item.ndc}</td>
                      <td>{item.qtyOrdered?.toFixed(2)}</td>
                      <td>{item.qtyshipped?.toFixed(2)}</td>
                      <td>{item.uom}</td>
                      <td>{item.qtyShippedInvUom?.toFixed(2)}</td>
                      <td>{item.qtyReceived?.toFixed(2)}</td>
                      <td>{item.invUom}</td>
                      <td>{item.itemDescription}</td>
                      <td>{item.expDate}</td>
                      <td>{item.processed ? "Yes" : "No"}</td>
                      <td>{item.reason}</td>
                      <td className="action-cell">
                        <span
                          className="action-link"
                          onClick={() => handleFetch(item)}
                        >
                          Fetch
                        </span>
                        <span
                          className="action-link"
                          onClick={() => handleMarkProcessed(item)}
                        >
                          Mark Processed
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={16} className="text-center">
                      No data items found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {/* Existing Barcodes Popup */}
      {showBarcodePopup && (
        <ExistingBarcodesModal
          barcodeItems={barcodeItems}
          onSave={handleBarcodeSave}
          onClose={handleBarcodeCancel}
          onNewBarcodeSave={handleNewBarcodeSave}
        />
      )}
    </div>
  );
};

export default McOrderProcessor;
